import isEmpty from "lodash/isEmpty";
import { PostUntilData } from "PFApp/components/expiry_date_modal/expiry_date_modal.types";
import { OTHER_REASON_KEY } from "PFApp/helpers/activity_form/post_until_change_reason";
import { formatEndOfDay, utc } from "PFCore/helpers/date";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { PostUntilChangeReason, Template } from "PFTypes";
import { Option } from "PFTypes/options";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ReasonsForm } from "./expiry_date_change_fields.types";

type UseExpiryDateChangeFormParams = {
  date?: string;
  reason?: PostUntilChangeReason | null;
  template: Template;
};

export const useExpiryDateChangeForm = ({ date, reason, template }: UseExpiryDateChangeFormParams) => {
  const { t } = useTranslation("translation");
  const { formatISODate } = useDateFormatter();
  const tomorrow = utc().add(1, "days");
  const minDate = formatISODate(tomorrow);

  const buildDefaultValues = (): ReasonsForm => ({
    date: formatISODate(date ?? tomorrow),
    reason: reason?.key ?? "",
    otherReason: reason?.key === OTHER_REASON_KEY ? reason?.text : ""
  });

  const { control, handleSubmit, watch, reset } = useForm<ReasonsForm>({
    defaultValues: buildDefaultValues()
  });

  const resetForm = () => reset(buildDefaultValues());

  const [watchedReason, watchedOtherReason] = watch(["reason", "otherReason"]);
  const isOtherReason = watchedReason === OTHER_REASON_KEY;
  const reasonsList = useMemo(() => template?.localized_post_until_change_reasons ?? [], [template]);
  const isReasonSelectorEnabled = !isEmpty(reasonsList);

  const dropdownOptions: Option<string>[] = useMemo(
    () =>
      reasonsList.map(({ key, text }) => {
        const displayElement = key === OTHER_REASON_KEY ? t("other") : text;
        return { id: key, value: displayElement, original: key, displayElement };
      }),
    [reasonsList, t]
  );

  const buildPostUntilData = (formData: ReasonsForm): PostUntilData => {
    const { date, reason, otherReason } = formData;
    const text =
      reason !== OTHER_REASON_KEY ? reasonsList.find(({ key }) => key === reason)?.text : otherReason;

    return {
      date: formatEndOfDay(date),
      ...(isReasonSelectorEnabled ? { reason: { key: reason, text } } : {})
    };
  };

  const shouldEnableSave =
    !isReasonSelectorEnabled ||
    (!!watchedReason && watchedReason !== OTHER_REASON_KEY) ||
    (watchedReason === OTHER_REASON_KEY && !!watchedOtherReason?.trim());

  return {
    control,
    handleSubmit,
    resetForm,
    minDate,
    isReasonSelectorEnabled,
    isOtherReason,
    dropdownOptions,
    buildPostUntilData,
    shouldEnableSave
  };
};
